import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  Calendar,
  Clock,
  Moon,
  Sun,
  Bell,
  Bookmark,
  BookmarkX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateTimeHeader from "../components/DateTimeHeader";
import Dropdown from "../components/Dropdown";
import NavBar from "../components/NavBar";
import { useTheme } from "../contexts/ThemeContext";

interface Note {
  id: string;
  title: string;
  content: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  isPinned: boolean;
  priority: "low" | "medium" | "high" | "none";
  contentSections?: { id: string; content: string; name: string }[];
}

const Notes: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  // const [darkMode, setDarkMode] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [newNote, setNewNote] = useState({
    title: "",
    content: "",
    category: "personal",
    priority: "none" as "low" | "medium" | "high" | "none",
    contentSections: [{ id: "1", content: "", name: "Section 1" }],
  });
  const [newCategory, setNewCategory] = useState({
    name: "",
    color: "blue",
  });
  const [expandedPriority, setExpandedPriority] = useState<string | null>(null);
  const [hoveredPriority, setHoveredPriority] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [showNoteDetails, setShowNoteDetails] = useState<string | null>(null);
      const { darkMode } = useTheme();
  

  const [categories, setCategories] = useState([
    { value: "all", label: "All Notes", color: "gray" },
    { value: "personal", label: "Personal", color: "blue" },
    { value: "work", label: "Work", color: "green" },
    { value: "ideas", label: "Ideas", color: "purple" },
    { value: "tasks", label: "Tasks", color: "orange" },
    { value: "important", label: "Important", color: "red" },
  ]);

  const colorOptions = [
    { value: "blue", label: "Blue", class: "bg-blue-500" },
    { value: "green", label: "Green", class: "bg-green-500" },
    { value: "purple", label: "Purple", class: "bg-purple-500" },
    { value: "orange", label: "Orange", class: "bg-orange-500" },
    { value: "red", label: "Red", class: "bg-red-500" },
    { value: "pink", label: "Pink", class: "bg-pink-500" },
  ];

  // Sample notifications
  const notifications = [
    {
      id: 1,
      icon: <Calendar className="text-blue-400" size={18} />,
      text: "Team meeting in 30 minutes",
    },
    {
      id: 2,
      icon: <Clock className="text-green-400" size={18} />,
      text: "Project deadline tomorrow",
    },
    {
      id: 3,
      icon: <Bell className="text-yellow-400" size={18} />,
      text: "New message from Sarah",
    },
  ];

  useEffect(() => {
    // Load sample notes
    const sampleNotes: Note[] = [
      {
        id: "1",
        title: "Meeting Notes - Q4 Planning",
        content:
          "Discussed Q4 goals and objectives. Need to focus on user engagement metrics and feature development timeline.",
        category: "work",
        createdAt: "2024-01-15T10:00:00Z",
        updatedAt: "2024-01-15T10:00:00Z",
        isPinned: true,
        priority: "high",
      },
      {
        id: "2",
        title: "App Feature Ideas",
        content:
          "Dark mode toggle, push notifications, offline support, voice notes, collaboration features.",
        category: "ideas",
        createdAt: "2024-01-14T14:30:00Z",
        updatedAt: "2024-01-14T14:30:00Z",
        isPinned: false,
        priority: "medium",
      },
      {
        id: "3",
        title: "Shopping List",
        content:
          "Milk, bread, eggs, bananas, coffee, laundry detergent, paper towels.",
        category: "personal",
        createdAt: "2024-01-13T09:15:00Z",
        updatedAt: "2024-01-13T09:15:00Z",
        isPinned: false,
        priority: "low",
      },
    ];
    setNotes(sampleNotes);
    setFilteredNotes(sampleNotes);
  }, []);

  useEffect(() => {
    let filtered = notes;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (note) =>
          note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          note.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((note) => note.category === selectedCategory);
    }

    // Filter by priority/status
    if (selectedFilter !== "all") {
      switch (selectedFilter) {
        case "high-priority":
          filtered = filtered.filter((note) => note.priority === "high");
          break;
        case "medium-priority":
          filtered = filtered.filter((note) => note.priority === "medium");
          break;
        case "low-priority":
          filtered = filtered.filter((note) => note.priority === "low");
          break;
        case "no-priority":
          filtered = filtered.filter((note) => note.priority === "none");
          break;
        case "pinned":
          filtered = filtered.filter((note) => note.isPinned);
          break;
        case "recent":
          filtered = filtered.filter((note) => {
            const noteDate = new Date(note.updatedAt);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return noteDate > weekAgo;
          });
          break;
        default:
          break;
      }
    }

    setFilteredNotes(filtered);
  }, [notes, searchTerm, selectedCategory, selectedFilter]);

  // Auto-collapse expanded priority when not hovering
  useEffect(() => {
    if (expandedPriority && !hoveredPriority) {
      const timer = setTimeout(() => {
        setExpandedPriority(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [expandedPriority, hoveredPriority]);

  // Auto-collapse expanded category when not hovering
  useEffect(() => {
    if (expandedCategory && !hoveredCategory) {
      const timer = setTimeout(() => {
        setExpandedCategory(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [expandedCategory, hoveredCategory]);

  const handleAddNote = () => {
    if (!newNote.title.trim()) return;

    const note: Note = {
      id: Date.now().toString(),
      title: newNote.title,
      content: newNote.contentSections
        .map((section) => section.content)
        .join("\n\n"),
      category: newNote.category,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isPinned: false,
      priority: newNote.priority,
      contentSections: newNote.contentSections,
    };

    setNotes([note, ...notes]);
    setNewNote({
      title: "",
      content: "",
      category: "personal",
      priority: "none",
      contentSections: [{ id: "1", content: "", name: "Section 1" }],
    });
    setShowAddModal(false);
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setNewNote({
      title: note.title,
      content: note.content,
      category: note.category,
      priority: note.priority,
      contentSections: note.contentSections || [
        { id: "1", content: note.content, name: "Section 1" },
      ],
    });
    setShowAddModal(true);
  };

  const handleUpdateNote = () => {
    if (!editingNote || !newNote.title.trim()) return;

    const updatedNote: Note = {
      ...editingNote,
      title: newNote.title,
      content: newNote.contentSections
        .map((section) => section.content)
        .join("\n\n"),
      category: newNote.category,
      priority: newNote.priority,
      contentSections: newNote.contentSections,
      updatedAt: new Date().toISOString(),
    };

    setNotes(
      notes.map((note) => (note.id === editingNote.id ? updatedNote : note))
    );
    setEditingNote(null);
    setNewNote({
      title: "",
      content: "",
      category: "personal",
      priority: "none",
      contentSections: [{ id: "1", content: "", name: "Section 1" }],
    });
    setShowAddModal(false);
  };

  const handleDeleteNote = (id: string) => {
    setNotes(notes.filter((note) => note.id !== id));
  };

  const handleTogglePin = (id: string) => {
    setNotes(
      notes.map((note) =>
        note.id === id ? { ...note, isPinned: !note.isPinned } : note
      )
    );
  };

  const handleTogglePriority = (id: string) => {
    setNotes(
      notes.map((note) => {
        if (note.id === id) {
          // Cycle through priority levels: none -> low -> medium -> high -> none
          const priorityOrder: ("none" | "low" | "medium" | "high")[] = [
            "none",
            "low",
            "medium",
            "high",
          ];
          const currentIndex = priorityOrder.indexOf(note.priority);
          const nextIndex = (currentIndex + 1) % priorityOrder.length;
          const newPriority = priorityOrder[nextIndex];

          // Expand to show the new priority
          setExpandedPriority(id);

          return { ...note, priority: newPriority };
        }
        return note;
      })
    );
  };

  const handleToggleCategory = (id: string) => {
    setNotes(
      notes.map((note) => {
        if (note.id === id) {
          // Get available categories (excluding 'all')
          const availableCategories = categories
            .filter((c) => c.value !== "all")
            .map((c) => c.value);
          const currentIndex = availableCategories.indexOf(note.category);
          const nextIndex = (currentIndex + 1) % availableCategories.length;
          const newCategory = availableCategories[nextIndex];

          // Expand to show the new category
          setExpandedCategory(id);

          return { ...note, category: newCategory };
        }
        return note;
      })
    );
  };

  const handleAddCategory = () => {
    if (!newCategory.name.trim()) return;

    const categoryValue = newCategory.name.toLowerCase().replace(/\s+/g, "-");
    const newCat = {
      value: categoryValue,
      label: newCategory.name,
      color: newCategory.color,
    };

    setCategories([...categories, newCat]);
    setNewCategory({ name: "", color: "blue" });
    setShowCategoryModal(false);
  };

  const handleDeleteCategory = (categoryValue: string) => {
    if (categoryValue === "all") return; // Can't delete "All Notes"

    // Remove category from categories
    setCategories(categories.filter((cat) => cat.value !== categoryValue));

    // Update notes that use this category to 'personal'
    setNotes(
      notes.map((note) =>
        note.category === categoryValue
          ? { ...note, category: "personal" }
          : note
      )
    );

    // If this category was selected, switch to 'all'
    if (selectedCategory === categoryValue) {
      setSelectedCategory("all");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCategoryColor = (category: string) => {
    const cat = categories.find((c) => c.value === category);
    return cat ? cat.color : "gray";
  };

  const addContentSection = () => {
    const newSectionId = (newNote.contentSections.length + 1).toString();
    setNewNote({
      ...newNote,
      contentSections: [
        ...newNote.contentSections,
        { id: newSectionId, content: "", name: `Section ${newSectionId}` },
      ],
    });
  };

  const removeContentSection = (sectionId: string) => {
    if (newNote.contentSections.length > 1) {
      setNewNote({
        ...newNote,
        contentSections: newNote.contentSections.filter(
          (section) => section.id !== sectionId
        ),
      });
    }
  };

  const updateContentSection = (sectionId: string, content: string) => {
    setNewNote({
      ...newNote,
      contentSections: newNote.contentSections.map((section) =>
        section.id === sectionId ? { ...section, content } : section
      ),
    });
  };

  const updateSectionName = (sectionId: string, name: string) => {
    setNewNote({
      ...newNote,
      contentSections: newNote.contentSections.map((section) =>
        section.id === sectionId ? { ...section, name } : section
      ),
    });
  };

  return (
    <>
      <div
        className={`min-h-screen ${
          darkMode
            ? "bg-gradient-to-br from-[#181824] to-[#232336]"
            : "bg-gradient-to-br from-gray-50 to-blue-50"
        }`}
      >
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        <main
          className={`flex-1 px-6 py-8 md:px-12 md:py-10 transition-all duration-200 ${
            collapsed ? "ml-20" : "ml-20 md:ml-52"
          }`}
        >
          {/* Top Bar */}
          <NavBar />

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
              <div>
                <h1
                  className={`text-3xl font-bold mb-2 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  My Notes
                </h1>
                <p
                  className={`text-lg ${
                    darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                >
                  {filteredNotes.length} note
                  {filteredNotes.length !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    darkMode
                      ? "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/20"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  Manage Categories
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                  }`}
                >
                  <Plus size={20} />
                  Add Note
                </button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                      darkMode ? "text-white/40" : "text-gray-400"
                    }`}
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search notes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode
                        ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                        : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Filter Dropdown */}
                <div className="md:w-48">
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-white border-gray-200 text-gray-900"
                    }`}
                  >
                    <option value="all">All Notes</option>
                    <option value="high-priority">High Priority</option>
                    <option value="medium-priority">Medium Priority</option>
                    <option value="low-priority">Low Priority</option>
                    <option value="no-priority">No Priority</option>
                    <option value="pinned">Pinned Notes</option>
                    <option value="recent">Recent (Last 7 days)</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 items-center flex-wrap">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category.value
                        ? "bg-blue-600 text-white shadow-lg"
                        : darkMode
                        ? "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => setShowNoteDetails(note.id)}
                className={`group relative rounded-2xl p-6 border transition-all duration-300 hover:scale-105 cursor-pointer ${
                  darkMode
                    ? "bg-white/5 border-white/10 hover:border-white/20"
                    : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
                }`}
              >
                {/* Color Accent */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                  style={{
                    backgroundColor:
                      colorOptions.find(
                        (c) =>
                          c.value ===
                          categories.find((cat) => cat.value === note.category)
                            ?.color
                      )?.class === "bg-blue-500"
                        ? "#3B82F6"
                        : colorOptions.find(
                            (c) =>
                              c.value ===
                              categories.find(
                                (cat) => cat.value === note.category
                              )?.color
                          )?.class === "bg-green-500"
                        ? "#10B981"
                        : colorOptions.find(
                            (c) =>
                              c.value ===
                              categories.find(
                                (cat) => cat.value === note.category
                              )?.color
                          )?.class === "bg-purple-500"
                        ? "#8B5CF6"
                        : colorOptions.find(
                            (c) =>
                              c.value ===
                              categories.find(
                                (cat) => cat.value === note.category
                              )?.color
                          )?.class === "bg-orange-500"
                        ? "#F59E0B"
                        : colorOptions.find(
                            (c) =>
                              c.value ===
                              categories.find(
                                (cat) => cat.value === note.category
                              )?.color
                          )?.class === "bg-red-500"
                        ? "#EF4444"
                        : colorOptions.find(
                            (c) =>
                              c.value ===
                              categories.find(
                                (cat) => cat.value === note.category
                              )?.color
                          )?.class === "bg-pink-500"
                        ? "#EC4899"
                        : "#6B7280",
                  }}
                ></div>

                {/* Pin Button - Top Right */}
                <div className="absolute top-4 right-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTogglePin(note.id);
                    }}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                    }`}
                    title={note.isPinned ? "Unpin note" : "Pin note"}
                  >
                    {note.isPinned ? (
                      <Bookmark
                        className={`w-4 h-4 ${
                          darkMode ? "text-yellow-400" : "text-yellow-600"
                        }`}
                      />
                    ) : (
                      <BookmarkX
                        className={`w-4 h-4 ${
                          darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      />
                    )}
                  </button>
                </div>

                {/* Category and Priority */}
                <div className="mb-3 flex items-center gap-2">
                  <div
                    onMouseEnter={() => {
                      setHoveredCategory(note.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredCategory(null);
                      // Collapse after a longer delay when leaving
                      setTimeout(() => {
                        setExpandedCategory(null);
                      }, 3000);
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCategory(note.id);
                      }}
                      className={`flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer h-6 ${
                        darkMode
                          ? "bg-white/10 text-white/60 hover:bg-white/20"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      } ${
                        expandedCategory === note.id
                          ? "px-3 py-2 shadow-lg"
                          : ""
                      }`}
                      title={`Click to change category`}
                    >
                      <span>
                        {
                          categories.find((c) => c.value === note.category)
                            ?.label
                        }
                      </span>
                    </button>
                  </div>

                  {/* Priority Button */}
                  <div
                    onMouseEnter={() => {
                      setHoveredPriority(note.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredPriority(null);
                      // Collapse after a longer delay when leaving
                      setTimeout(() => {
                        setExpandedPriority(null);
                      }, 3000);
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePriority(note.id);
                      }}
                      className={`flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer h-6 ${
                        note.priority === "high"
                          ? "hover:bg-red-600 dark:hover:bg-red-700"
                          : note.priority === "medium"
                          ? "hover:bg-orange-600 dark:hover:bg-orange-700"
                          : note.priority === "low"
                          ? "hover:bg-green-600 dark:hover:bg-green-700"
                          : "hover:bg-gray-600 dark:hover:bg-gray-700"
                      } ${
                        expandedPriority === note.id
                          ? "px-3 py-2 shadow-lg"
                          : ""
                      } ${
                        note.priority === "high"
                          ? "bg-red-500 text-white dark:bg-red-600 dark:text-white"
                          : note.priority === "medium"
                          ? "bg-orange-500 text-white dark:bg-orange-600 dark:text-white"
                          : note.priority === "low"
                          ? "bg-green-500 text-white dark:bg-green-600 dark:text-white"
                          : "bg-gray-500 text-white dark:bg-gray-600 dark:text-white"
                      }`}
                      title={`Click to change priority`}
                    >
                      <span>
                        {note.priority === "none"
                          ? "None"
                          : note.priority.charAt(0).toUpperCase() +
                            note.priority.slice(1)}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Title */}
                <div className="mb-1">
                  <h3
                    className={`text-lg font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {note.title}
                  </h3>
                </div>

                {/* Date */}
                <div className="mb-3">
                  <span
                    className={`text-xs ${
                      darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                  >
                    {formatDate(note.updatedAt)}
                  </span>
                </div>

                {/* Note Content */}
                <div className="mb-4">
                  <p
                    className={`text-sm leading-relaxed line-clamp-3 font-medium ${
                      darkMode ? "text-white/90" : "text-gray-800"
                    }`}
                  >
                    {note.content}
                  </p>
                </div>

                {/* Actions - Right Side */}
                <div className="absolute right-4 bottom-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditNote(note);
                    }}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                    }`}
                    title="Edit note"
                  >
                    <Edit
                      size={16}
                      className={darkMode ? "text-white/60" : "text-gray-600"}
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(note.id);
                    }}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      darkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"
                    }`}
                    title="Delete note"
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredNotes.length === 0 && (
            <div className="text-center py-16">
              <div
                className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${
                  darkMode ? "bg-white/5" : "bg-gray-100"
                }`}
              >
                <Plus
                  size={32}
                  className={`${darkMode ? "text-white/40" : "text-gray-400"}`}
                />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                No notes found
              </h3>
              <p className={`${darkMode ? "text-white/60" : "text-gray-600"}`}>
                {searchTerm || selectedCategory !== "all"
                  ? "Try adjusting your search or filters"
                  : "Create your first note to get started"}
              </p>
              {!searchTerm && selectedCategory === "all" && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className={`mt-4 flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                  }`}
                >
                  <Plus size={20} />
                  Create Note
                </button>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Add/Edit Note Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => {
            setShowAddModal(false);
            setEditingNote(null);
            setNewNote({
              title: "",
              content: "",
              category: "personal",
              priority: "none",
              contentSections: [{ id: "1", content: "", name: "Section 1" }],
            });
          }}
        >
          <div
            className={`${
              darkMode ? "bg-[#232336]" : "bg-white"
            } rounded-2xl shadow-2xl p-8 w-[90vw] max-w-2xl relative border ${
              darkMode ? "border-white/10" : "border-gray-200"
            } max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-200 ${
                darkMode
                  ? "text-white/60 hover:text-white hover:bg-white/10"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => {
                setShowAddModal(false);
                setEditingNote(null);
                setNewNote({
                  title: "",
                  content: "",
                  category: "personal",
                  priority: "none",
                  contentSections: [
                    { id: "1", content: "", name: "Section 1" },
                  ],
                });
              }}
            >
              <X size={24} />
            </button>

            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {editingNote ? "Edit Note" : "Add New Note"}
              </h2>
              <p
                className={`text-sm ${
                  darkMode ? "text-white/60" : "text-gray-600"
                }`}
              >
                {editingNote
                  ? "Update your note details"
                  : "Create a new personal note"}
              </p>
            </div>

            <div className="space-y-6 flex-1 overflow-y-auto">
              {/* Title */}
              <div className="flex-shrink-0">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-white/70" : "text-gray-700"
                  }`}
                >
                  Title
                </label>
                <input
                  type="text"
                  placeholder="Enter note title..."
                  value={newNote.title}
                  onChange={(e) =>
                    setNewNote({ ...newNote, title: e.target.value })
                  }
                  className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode
                      ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                      : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Content Sections */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`block text-sm font-medium ${
                      darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                  >
                    Content Sections
                  </label>
                  <button
                    type="button"
                    onClick={addContentSection}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                      darkMode
                        ? "bg-white/10 text-white hover:bg-white/20"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    + Add Section
                  </button>
                </div>

                <div className="space-y-3">
                  {newNote.contentSections.map((section, index) => (
                    <div key={section.id} className="space-y-3">
                      {/* Section Title */}
                      <div className="flex items-center justify-between">
                        <input
                          type="text"
                          value={section.name}
                          onChange={(e) =>
                            updateSectionName(section.id, e.target.value)
                          }
                          className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            darkMode
                              ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                              : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder={`Section ${index + 1}`}
                          title="Click to edit section name"
                        />
                        {newNote.contentSections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContentSection(section.id)}
                            className={`p-1 rounded transition-all duration-200 ${
                              darkMode
                                ? "text-white/40 hover:text-red-400 hover:bg-red-500/10"
                                : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                            }`}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Content Container */}
                      <textarea
                        placeholder={`Write content for section ${
                          index + 1
                        }...`}
                        rows={4}
                        value={section.content}
                        onChange={(e) =>
                          updateContentSection(section.id, e.target.value)
                        }
                        className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                          darkMode
                            ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                            : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Category and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                  >
                    Category
                  </label>
                  <select
                    value={newNote.category}
                    onChange={(e) =>
                      setNewNote({ ...newNote, category: e.target.value })
                    }
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-900"
                    }`}
                  >
                    {categories
                      .filter((c) => c.value !== "all")
                      .map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                  >
                    Priority
                  </label>
                  <select
                    value={newNote.priority}
                    onChange={(e) =>
                      setNewNote({
                        ...newNote,
                        priority: e.target.value as
                          | "low"
                          | "medium"
                          | "high"
                          | "none",
                      })
                    }
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-900"
                    }`}
                  >
                    <option value="none">No Priority</option>
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
                <button
                  onClick={editingNote ? handleUpdateNote : handleAddNote}
                  disabled={!newNote.title.trim()}
                  className={`flex-1 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                    newNote.title.trim()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {editingNote ? "Update Note" : "Create Note"}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingNote(null);
                    setNewNote({
                      title: "",
                      content: "",
                      category: "personal",
                      priority: "none",
                      contentSections: [
                        { id: "1", content: "", name: "Section 1" },
                      ],
                    });
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    darkMode
                      ? "border border-white/20 text-white hover:bg-white/10"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => {
            setShowCategoryModal(false);
            setNewCategory({ name: "", color: "blue" });
          }}
        >
          <div
            className={`${
              darkMode ? "bg-[#232336]" : "bg-white"
            } rounded-2xl shadow-2xl p-8 w-[90vw] max-w-2xl relative border ${
              darkMode ? "border-white/10" : "border-gray-200"
            } max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-200 ${
                darkMode
                  ? "text-white/60 hover:text-white hover:bg-white/10"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => {
                setShowCategoryModal(false);
                setNewCategory({ name: "", color: "blue" });
              }}
            >
              <X size={24} />
            </button>

            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Manage Categories
              </h2>
              <p
                className={`text-sm ${
                  darkMode ? "text-white/60" : "text-gray-600"
                }`}
              >
                Create and manage your note categories
              </p>
            </div>

            <div className="space-y-6">
              {/* Add New Category */}
              <div
                className={`p-6 rounded-xl border ${
                  darkMode
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Add New Category
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                    >
                      Category Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter category name..."
                      value={newCategory.name}
                      onChange={(e) =>
                        setNewCategory({ ...newCategory, name: e.target.value })
                      }
                      className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                          : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                    >
                      Color
                    </label>
                    <div className="flex gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.value}
                          onClick={() =>
                            setNewCategory({
                              ...newCategory,
                              color: color.value,
                            })
                          }
                          className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                            newCategory.color === color.value
                              ? "border-white shadow-lg scale-110"
                              : darkMode
                              ? "border-white/20"
                              : "border-gray-300"
                          }`}
                          style={{
                            backgroundColor:
                              color.value === "blue"
                                ? "#3B82F6"
                                : color.value === "green"
                                ? "#10B981"
                                : color.value === "purple"
                                ? "#8B5CF6"
                                : color.value === "orange"
                                ? "#F59E0B"
                                : color.value === "red"
                                ? "#EF4444"
                                : color.value === "pink"
                                ? "#EC4899"
                                : "#6B7280",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategory.name.trim()}
                  className={`w-full py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                    newCategory.name.trim()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  Add Category
                </button>
              </div>

              {/* Existing Categories */}
              <div>
                <h3
                  className={`text-lg font-semibold mb-4 ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Existing Categories
                </h3>
                <div className="space-y-3">
                  {categories
                    .filter((cat) => cat.value !== "all")
                    .map((category) => (
                      <div
                        key={category.value}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{
                              backgroundColor:
                                colorOptions.find(
                                  (c) => c.value === category.color
                                )?.class === "bg-blue-500"
                                  ? "#3B82F6"
                                  : colorOptions.find(
                                      (c) => c.value === category.color
                                    )?.class === "bg-green-500"
                                  ? "#10B981"
                                  : colorOptions.find(
                                      (c) => c.value === category.color
                                    )?.class === "bg-purple-500"
                                  ? "#8B5CF6"
                                  : colorOptions.find(
                                      (c) => c.value === category.color
                                    )?.class === "bg-orange-500"
                                  ? "#F59E0B"
                                  : colorOptions.find(
                                      (c) => c.value === category.color
                                    )?.class === "bg-red-500"
                                  ? "#EF4444"
                                  : colorOptions.find(
                                      (c) => c.value === category.color
                                    )?.class === "bg-pink-500"
                                  ? "#EC4899"
                                  : "#6B7280",
                            }}
                          />
                          <span
                            className={`font-medium ${
                              darkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {category.label}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              darkMode
                                ? "bg-white/10 text-white/60"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {
                              notes.filter(
                                (note) => note.category === category.value
                              ).length
                            }{" "}
                            notes
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteCategory(category.value)}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            darkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"
                          }`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  {categories.filter((cat) => cat.value !== "all").length ===
                    0 && (
                    <div
                      className={`text-center py-8 ${
                        darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                    >
                      No custom categories created yet
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-200 dark:border-white/10">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setNewCategory({ name: "", color: "blue" });
                  }}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
                    darkMode
                      ? "border border-white/20 text-white hover:bg-white/10"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            className={`rounded-2xl p-6 max-w-md w-full mx-4 ${
              darkMode ? "bg-[#232336]" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3
                className={`text-xl font-bold mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Delete Note
              </h3>
              <p
                className={`text-sm mb-6 ${
                  darkMode ? "text-white/70" : "text-gray-600"
                }`}
              >
                Are you sure you want to delete this note? This action cannot be
                undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
                    darkMode
                      ? "border border-white/20 text-white hover:bg-white/10"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDeleteNote(showDeleteConfirm);
                    setShowDeleteConfirm(null);
                  }}
                  className="flex-1 py-3 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-all duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note Details Modal */}
      {showNoteDetails && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowNoteDetails(null)}
        >
          <div
            className={`rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
              darkMode ? "bg-[#232336]" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const note = notes.find((n) => n.id === showNoteDetails);
              if (!note) return null;

              return (
                <>
                  <div className="flex items-center justify-between mb-6 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          darkMode
                            ? "bg-white/10 text-white/60"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {
                          categories.find((c) => c.value === note.category)
                            ?.label
                        }
                      </span>
                      {note.priority !== "none" && (
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            note.priority === "high"
                              ? "bg-red-100 text-red-700"
                              : note.priority === "medium"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {note.priority.charAt(0).toUpperCase() +
                            note.priority.slice(1)}{" "}
                          Priority
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setShowNoteDetails(null)}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                      }`}
                    >
                      <X
                        size={20}
                        className={darkMode ? "text-white/60" : "text-gray-600"}
                      />
                    </button>
                  </div>

                  <h2
                    className={`text-2xl font-bold mb-4 flex-shrink-0 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {note.title}
                  </h2>

                  <div
                    className={`text-sm mb-6 flex-shrink-0 ${
                      darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                  >
                    {formatDate(note.updatedAt)}
                  </div>

                  {/* Note Content Containers */}
                  <div className="space-y-4 flex-1 overflow-y-auto">
                    {note.contentSections && note.contentSections.length > 0 ? (
                      note.contentSections.map((section, index) => (
                        <div key={section.id} className="space-y-3">
                          {/* Section Title */}
                          <span
                            className={`text-sm font-semibold pl-2 ${
                              darkMode ? "text-white/80" : "text-gray-700"
                            }`}
                          >
                            {section.name || `Section ${index + 1}`}
                          </span>

                          {/* Content Container */}
                          <div
                            className={`p-4 rounded-lg border ${
                              darkMode
                                ? "bg-white/5 border-white/10"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-sm leading-relaxed whitespace-pre-wrap ${
                                darkMode ? "text-white/90" : "text-gray-800"
                              }`}
                            >
                              {section.content}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        className={`p-4 rounded-lg border ${
                          darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm leading-relaxed whitespace-pre-wrap ${
                            darkMode ? "text-white/90" : "text-gray-800"
                          }`}
                        >
                          {note.content}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      {note.isPinned && (
                        <Bookmark
                          className={`w-4 h-4 ${
                            darkMode ? "text-yellow-400" : "text-yellow-600"
                          }`}
                        />
                      )}
                      <span
                        className={`text-xs ${
                          darkMode ? "text-white/40" : "text-gray-500"
                        }`}
                      >
                        {note.isPinned ? "Pinned" : "Not pinned"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          handleEditNote(note);
                          setShowNoteDetails(null);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          darkMode
                            ? "bg-white/10 text-white hover:bg-white/20"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(note.id);
                          setShowNoteDetails(null);
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-all duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default Notes;
